// Phase 4 — single source of truth mapping admin routes ⇄ internal tab ids.
// The URL drives the active section; nothing else stores tab state.

export const ADMIN_SECTIONS = [
  { route: "jobs", tab: "jobs", label: "Jobs", subtitle: "Manage enquiries, customers & schedule" },
  { route: "site-mode", tab: "site-mode", label: "Site Mode", subtitle: "Quick log from the field" },
  { route: "calendar", tab: "calendar", label: "Calendar", subtitle: "Day and week schedule" },
  { route: "customers", tab: "customers", label: "Customers", subtitle: "Customer records, jobs and duplicates" },
  { route: "quotes-invoices", tab: "quotes-invoices", label: "Quotes & Invoices", subtitle: "Quotes, invoicing and payments" },
  { route: "gilberts", tab: "gilberts", label: "Gilberts", subtitle: "Gilberts shifts and rates" },
  { route: "lightbox", tab: "portfolio", label: "Lightbox", subtitle: "Portfolio photos and gallery publishing" },
  { route: "feedback", tab: "feedback", label: "Feedback", subtitle: "Customer feedback and reviews" },
  { route: "analytics", tab: "analytics", label: "Analytics", subtitle: "Business performance and metrics" },
  { route: "ai-assistant", tab: "ai-assistant", label: "AI Assistant", subtitle: "AI-powered business assistant" },
  { route: "materials", tab: "materials", label: "Materials", subtitle: "Materials tracking and receipts" },
  { route: "site-editor", tab: "site-editor", label: "Site Editor", subtitle: "Public website content" },
  { route: "settings", tab: "settings", label: "Settings", subtitle: "Account and integrations" },
];

const BY_ROUTE = Object.fromEntries(ADMIN_SECTIONS.map((s) => [s.route, s]));
const BY_TAB = Object.fromEntries(ADMIN_SECTIONS.map((s) => [s.tab, s]));

export const routeToTab = (route) => BY_ROUTE[route]?.tab ?? null;
export const tabToRoute = (tab) => BY_TAB[tab]?.route ?? null;
export const sectionForTab = (tab) => BY_TAB[tab] ?? null;
export const documentTitleForTab = (tab) =>
  BY_TAB[tab]
    ? `${BY_TAB[tab].label} | Sustainable Handyman Services Admin`
    : "Page not found | Sustainable Handyman Services Admin";

// Query parameters allowed to survive the legacy /admin/dashboard redirect and
// the post-login navigation. ONLY established, non-authentication deep-link /
// integration-callback parameters. Query parameters can NEVER authenticate an
// administrator and are never copied into storage, cookies or headers.
export const ALLOWED_REDIRECT_PARAMS = ["openJob", "openInvoice", "google_connected"];

const BLOCKED_PARAM_RE = /^(token|access_token|admin_token|admin_session|authorization|auth|jwt|bearer|code|session|api_key|apikey)$/i;

export const filterRedirectSearch = (search) => {
  const params = new URLSearchParams(search || "");
  const kept = new URLSearchParams();
  for (const name of ALLOWED_REDIRECT_PARAMS) {
    const value = params.get(name);
    if (value !== null && !BLOCKED_PARAM_RE.test(name)) kept.set(name, value);
  }
  const out = kept.toString();
  return out ? `?${out}` : "";
};

const VALID_ROUTES = new Set(ADMIN_SECTIONS.map((s) => s.route));

// Phase 5: safe post-login return. Only a relative internal admin route from
// the Phase 4 registry survives; absolute/protocol-relative/encoded-external
// URLs and traversal are rejected; auth-like params stripped; default /admin/jobs.
export const sanitizeReturnRoute = (raw) => {
  const DEFAULT = "/admin/jobs";
  if (!raw || typeof raw !== "string") return DEFAULT;
  let decoded;
  try { decoded = decodeURIComponent(raw); } catch { return DEFAULT; }
  if (!decoded.startsWith("/") || decoded.startsWith("//") || decoded.includes("://") ||
      decoded.includes("\\") || decoded.includes("..")) return DEFAULT;
  const [path, query = ""] = decoded.split("?");
  const parts = path.split("/"); // ["", "admin", "<section>"]
  if (parts[1] !== "admin" || !VALID_ROUTES.has(parts[2])) return DEFAULT;
  return `/admin/${parts[2]}${filterRedirectSearch(query ? `?${query}` : "")}`;
};
